import React, { useState } from "react";
import { ArrowLeft, CheckCircle2, Home, Loader2 } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { verifyUPI } from "@/services/customer";
import { getT } from "@/i18n/translations";


const UpiPage = () => {
  const t = getT();
  const navigate = useNavigate();
  const location = useLocation();
  const selectedAmount = location.state?.selectedAmount || 0;

  const [mobile, setMobile] = useState("");
  const [loading, setLoading] = useState(false);
  const [verified, setVerified] = useState(false);
  const [verifiedData, setVerifiedData] = useState(null);

  const handleSubmit = async () => {
    if (verified) {
      navigate("/upi-confirm", {
        state: {
          selectedAmount,
          mobile,
          upiId: verifiedData?.upi_handle,
          upiName: verifiedData?.name,
        },
      });
      return;
    }

    const mobileRegex = /^[6-9]\d{9}$/;
    if (!mobile || !mobileRegex.test(mobile)) {
      toast.error(t.upi_errInvalidMobile);
      return;
    }

    setLoading(true);
    try {
      const token = JSON.parse(localStorage.getItem("token_gen"));
      const res = await verifyUPI(token?.access_token, { mobileNumber: mobile, consent: true });
      const d = res?.data?.data;

      if (d?.is_valid === true) {
        toast.success(t.upi_successVerified);
        setVerifiedData(d);
        setVerified(true);
      } else {
        toast.error(d?.error_description || t.upi_errFailed);
      }
    } catch {
      toast.error(t.upi_errGeneric);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
    className="min-h-screen flex flex-col items-center"
      style={{
        fontFamily: "'Poppins', sans-serif",
        backgroundColor: '#f0f4f0',
        backgroundImage: 'radial-gradient(circle, #c8d8c8 1px, transparent 1px)',
        backgroundSize: '22px 22px',
      }}
    >
      {loading && (
        <div className="fixed inset-0 bg-black/20 flex justify-center items-center z-50 backdrop-blur-sm">
          <Loader2 className="animate-spin h-10 w-10 text-[#2d5a2d]" />
        </div>
      )}

 <div className="w-full max-w-md min-h-screen flex flex-col">

        {/* Header */}
        <div className="relative flex items-center justify-center px-4 py-4 mt-2">
          <button
            onClick={() => navigate(-1)}
            className="absolute left-4 w-9 h-9 flex items-center justify-center rounded-full bg-white shadow-sm"
          >
            <ArrowLeft className="w-5 h-5 text-[#1a2e1a]" />
          </button>
          <p className="text-[15px] font-semibold text-[#1a2e1a]">{t.upi_pageTitle}</p>
          <button
            onClick={() => navigate("/home")}
            className="absolute right-4 w-9 h-9 flex items-center justify-center rounded-full bg-white shadow-sm"
          >
            <Home className="w-5 h-5 text-[#1a2e1a]" />
          </button>
        </div>

     <div className="px-4 pb-8 space-y-3 flex-1">

          {/* Mobile input card */}
          <div className="bg-white rounded-2xl px-5 py-5 shadow-sm mx-1">
            <p className="text-[10px] font-semibold text-[#8a9e8a] uppercase tracking-widest mb-2">
              {t.upi_mobileLabel}
            </p>
            <div className="flex items-center border-[1.5px] border-[#e0e8e0] rounded-xl px-3.5 py-3 gap-2.5 focus-within:border-[#3a6b3a] transition-colors">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4a7c59" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
                <line x1="12" y1="18" x2="12.01" y2="18" strokeWidth="2.5" />
              </svg>
              <div className="w-px h-5 bg-[#d0dcd0]" />
              <input
                type="tel"
                inputMode="numeric"
                placeholder={t.upi_mobilePlaceholder}
                value={mobile}
                maxLength={10}
                disabled={verified}
                onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                className="flex-1 bg-transparent outline-none text-[15px] text-[#1a2e1a] placeholder:text-[#b0c4b0] placeholder:text-[13px] disabled:opacity-60"
              />
              {verified && <CheckCircle2 size={18} className="text-[#2d5a2d] shrink-0" />}
            </div>
          </div>

          {/* Verified info card */}
          {verified && verifiedData && (
            <div
              className="rounded-2xl px-5 py-4 shadow-sm mx-1"
              style={{ backgroundColor: '#eaf3de', border: '1.5px solid #a8d08a' }}
            >
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 size={15} className="text-[#2d5a2d] shrink-0" />
                <p className="text-[11px] font-semibold text-[#2d5a2d] uppercase tracking-wide">{t.upi_verifiedLabel}</p>
              </div>
              <div className="space-y-2">
                {[
                  { label: t.upi_name, value: verifiedData?.name },
                  { label: t.upi_upiId, value: verifiedData?.upi_handle },
                  { label: t.upi_mobile, value: verifiedData?.mobile_number },
                ].map(({ label, value }) => value ? (
                  <div key={label} className="flex justify-between items-center">
                    <p className="text-[12px] text-[#4a7c59]">{label}</p>
                    <p className="text-[13px] font-semibold text-[#1a2e1a]">{value}</p>
                  </div>
                ) : null)}
              </div>
            </div>
          )}

          {/* Button */}
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full py-4 rounded-xl text-[15px] font-bold text-white active:scale-[0.98] transition-transform disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ width: 'calc(100% - 8px)', marginLeft: '4px', backgroundColor: '#2d5a2d' }}
          >
            {loading ? t.upi_btnWait : verified ? t.upi_btnProceed : t.upi_btnVerify}
          </button>

         

        </div>
 <div className="w-full py-3 border-t border-gray-200 mt-auto">
          <p className="text-[13px] text-black uppercase text-center tracking-wide">
            © {new Date().getFullYear()} Gencrest
          </p>
        </div>
      </div>

    </div>
  );
};

export default UpiPage;